import { FormikActions } from 'formik';
import React, { ReactNode } from 'react';
import Form from '../../../app/common/components/Form';
import SwitchField from '../../../app/common/fields/SwitchField';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import {
    IAddress,
    IAddressRequest,
    IAddressUpdateRequest,
    IAddressFieldConfig,
} from '../address/address.interface';
import { AddressFormFields } from './AddressFormFields';
import { SupportedClientCountryCode } from '../../../config/constants/supportedClientCountryCode.enum';
import { FormManditoryAstrixMessage } from '../../../app/common/components/FormManitoryMessage';
import { getChallengeData } from '../../../utils/challengerMain';

interface IAddressFormProps {
    address?: IAddress;
    addAddress: (address: IAddressRequest) => any;
    isOnlyAddress: boolean;
    loading: boolean;
    updateAddress: (addressUuid: string, updatedAddress: IAddressUpdateRequest) => any;
    onSubmit: () => any;
    showWarning: (message: ReactNode) => any;
    defaultCountry: SupportedClientCountryCode;
    challengerDegenerate?: boolean;
    addressFieldConfig: IAddressFieldConfig[][];
    challengerPid: string;
}

interface IAddressFormState {
    initialValues: IAddressRequest;
    isEditing: boolean;
    isShippingAddress: boolean;
    isSubmitting: boolean;
}

class AddressForm extends React.Component<IAddressFormProps, IAddressFormState> {
    constructor(props: IAddressFormProps) {
        super(props);

        const { address, loading, isOnlyAddress, defaultCountry } = this.props;

        const initialValues: IAddressRequest = address
            ? {
                  firstName: address.firstName,
                  lastName: address.lastName,
                  companyName: address.companyName || '',
                  countryCode: address.countryCode,
                  addressLine1: address.addressLine1,
                  addressLine2: address.addressLine2 || '',
                  city: address.city,
                  cityCode: address.cityCode || '',
                  postalCode: address.postalCode,
                  phoneNumber: address.phoneNumber || '',
                  state: address.state,
                  stateCode: address.stateCode,
                  isShipping: address.isShipping,
              }
            : {
                  firstName: '',
                  lastName: '',
                  companyName: '',
                  countryCode: defaultCountry,
                  addressLine1: '',
                  addressLine2: '',
                  city: '',
                  cityCode: '',
                  postalCode: '',
                  phoneNumber: '',
                  state: '',
                  stateCode: '',
                  isShipping: isOnlyAddress,
              };

        this.state = {
            initialValues,
            isEditing: Boolean(address),
            isShippingAddress: Boolean(address && address.isShipping),
            isSubmitting: loading,
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.renderFormFields = this.renderFormFields.bind(this);
        this.renderExtraButtons = this.renderExtraButtons.bind(this);
    }

    public componentDidUpdate(prevProps: Readonly<IAddressFormProps>): void {
        const { loading } = this.props;
        if (loading !== prevProps.loading) {
            this.setState({ isSubmitting: loading });
        }
    }

    public handleSubmit(values: IAddressRequest, actions: FormikActions<IAddressRequest>) {
        const { onSubmit, address, addAddress, updateAddress } = this.props;
        const { isEditing } = this.state;

        if (this.props.challengerPid) {
            values = {
                ...values,
                challengeData: getChallengeData(this.props.challengerPid),
            };
        }

        if (isEditing && address) {
            updateAddress(address.addressUuid, {
                ...values,
                isShipping:
                    values.isShipping === address.isShipping ? undefined : values.isShipping,
            });
        } else {
            addAddress(values);
        }

        onSubmit();
    }

    public render() {
        const { showWarning, challengerDegenerate, challengerPid } = this.props;
        const { initialValues, isEditing, isSubmitting } = this.state;

        return (
            <Form
                buttonTextLabel={isEditing ? 'button.save' : 'button.add'}
                value={initialValues}
                handleSubmit={this.handleSubmit}
                loading={isSubmitting}
                renderFormFields={this.renderFormFields}
                renderExtraButtons={this.renderExtraButtons}
                showWarning={showWarning}
                saveButtonProps={{
                    qaId: 'submit-address',
                    width: '150px',
                }}
                challengerPid={challengerPid}
                qaId={`${isEditing ? 'edit' : 'create'}-address`}
            />
        );
    }

    private renderFormFields(values: IAddressRequest) {
        return (
            <>
                <FormManditoryAstrixMessage />
                <AddressFormFields
                    values={values}
                    addressFieldConfig={this.props.addressFieldConfig}
                />
            </>
        );
    }

    private renderExtraButtons(values: IAddressRequest) {
        const { isOnlyAddress } = this.props;
        const { isEditing, isShippingAddress } = this.state;

        return (
            <SwitchField
                id="isShipping"
                value={values.isShipping}
                disabled={(isEditing && isShippingAddress) || isOnlyAddress}
                labelMessage={TranslationHelpers.getMessageDescriptor('address.make_default')}
                tooltipText={
                    isEditing && isShippingAddress
                        ? TranslationHelpers.getMessageDescriptor('tooltip.default_address.edit')
                        : TranslationHelpers.getMessageDescriptor(
                              isOnlyAddress
                                  ? 'tooltip.default_address.create.only'
                                  : 'tooltip.default_address.create',
                          )
                }
                qaId="isShipping"
            />
        );
    }
}

export default AddressForm;
